import { styled, FormControlLabel, Switch as MuiSwitch } from '@mui/material';
import type { FormControlLabelProps, SwitchProps as MuiSwitchProps } from '@mui/material';
import type { ComponentType } from 'react';
import React from 'react';
import { colors } from 'styles/theme/colors';

export type SwitchProps = MuiSwitchProps & {
  label?: React.ReactNode;
  name?: string;
  labelColor?: React.CSSProperties['color'];
  margin?: React.CSSProperties['margin'];
  formControlLabelProps?: Omit<
    FormControlLabelProps,
    'disabled' | 'required' | 'control' | 'label'
  >;
};

/**
 * Switch component, part of Delogue design system.
 * @param label - optional label to display next to the switch
 * @param name - name attribute for the switch input element
 * @param onChange - callback function to be called when the switch value changes
 * @param onBlur - callback function to be called when the switch loses focus
 * @param size - size of the switch, either 'medium' or 'small', default is 'medium'
 * @param labelColor - color of the label text
 * @param margin - outer margin for the switch, default is 8
 * @param formControlLabelProps - additional props to pass to the FormControlLabel component
 * @param checked - whether the switch is checked
 * @param onKeyDown - callback function to be called when a key is pressed
 */
export const Switch = ({
  label,
  onChange,
  onBlur,
  size = 'medium',
  labelColor,
  margin = 8,
  formControlLabelProps,
  ...rest
}: SwitchProps) => {
  return label ? (
    <StyledControlLabel
      {...formControlLabelProps}
      disabled={rest?.disabled}
      required={rest?.required}
      control={
        <StyledSwitch
          checked={rest?.value as boolean}
          {...rest}
          margin={margin}
          size={size}
          onChange={(e, v) => {
            onChange?.(e, v);
          }}
          onBlur={e => {
            onBlur?.(e);
          }}
        />
      }
      color={labelColor}
      label={label}
    />
  ) : (
    <StyledSwitch
      checked={rest?.value as boolean}
      {...rest}
      margin={margin}
      size={size}
      onChange={(e, v) => {
        onChange?.(e, v);
      }}
      onBlur={e => {
        onBlur?.(e);
      }}
    />
  );
};

type StyledSwitchProps = MuiSwitchProps & {
  margin?: React.CSSProperties['margin'];
};

export const StyledSwitch: ComponentType<StyledSwitchProps> = styled(MuiSwitch, {
  shouldForwardProp: prop => prop !== 'margin',
})<StyledSwitchProps>(({ theme, size = 'medium', margin = 8 }) => {
  const isMedium = size === 'medium';

  const getValue = (mediumValue: number | string, smallValue: number | string) =>
    isMedium ? mediumValue : smallValue;

  const dimensions = {
    height: getValue(32, 20),
    thumbSize: getValue(16, 10),
    thumbActiveSize: getValue(24, 15),
    rootWidth: getValue(52, 32.5),
    basePadding: getValue(8, 5),
    activePadding: getValue(4, 2.5),
    focusPadding: getValue(4, 2.5),
    checkedPadding: getValue(4, 2.5),
    checkedActivePadding: getValue(2, 1.5),
    checkedFocusPadding: getValue(2, 1.5),
    checkedTransform: getValue('translateX(18px)', 'translateX(12.5px)'),
    checkedActiveThumbSize: getValue(28, 17.5),
    focusVisibleThumbSize: getValue(4, 17.5),
  };

  const commonThumbStyles = (color?: string, commonSize?: number | string) => ({
    width: commonSize,
    height: commonSize,
    minHeight: commonSize,
    minWidth: commonSize,
    backgroundColor: color,
  });

  return {
    width: dimensions.rootWidth,
    height: dimensions.height,
    padding: 0,
    margin,
    '& .MuiSwitch-switchBase': {
      padding: dimensions.basePadding,

      '&:hover .MuiSwitch-thumb': {
        backgroundColor: theme.palette.fg.secondary,
      },

      '&:active .MuiSwitch-thumb': {
        ...commonThumbStyles(theme.palette.fg.secondary, dimensions.thumbActiveSize),
      },
      '&:active': {
        padding: dimensions.activePadding,
      },

      '&:focus-visible .MuiSwitch-thumb': {
        ...commonThumbStyles(theme.palette.fg.secondary, dimensions.thumbActiveSize),
      },
      '&:focus-visible': {
        padding: dimensions.focusPadding,
      },

      '&.Mui-checked': {
        transform: dimensions.checkedTransform,
        padding: dimensions.checkedPadding,

        '& .MuiSwitch-thumb': {
          ...commonThumbStyles(theme.palette.bg.primary, dimensions.thumbActiveSize),
        },

        '&:active .MuiSwitch-thumb': {
          ...commonThumbStyles(theme.palette.bg.primary, dimensions.checkedActiveThumbSize),
        },
        '&:active': {
          padding: dimensions.checkedActivePadding,
        },

        '&:focus-visible .MuiSwitch-thumb': {
          ...commonThumbStyles(theme.palette.bg.primary, dimensions.focusVisibleThumbSize),
        },
        '&:focus-visible': {
          padding: dimensions.checkedFocusPadding,
        },

        '& + .MuiSwitch-track': {
          opacity: 'unset',
          border: 'unset',
        },
      },

      '&.Mui-disabled .MuiSwitch-thumb': {
        color: theme.palette.bg.primary,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
        background: theme.palette.border.disabledSubtle,
      },
    },

    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.fg.disabled,
      opacity: 0.38,
      border: `1px solid ${theme.palette.border.disabledSubtle}`,
    },

    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.7,
      background: theme.palette.bg.primary,
      border: `1px solid ${theme.palette.border.disabledSubtle}`,
    },

    '& .MuiSwitch-track': {
      borderRadius: (dimensions.height as number) / 2,
      background: theme.palette.bg.disabledSubtle,
      border: `1px solid ${theme.palette.border.primary}`,
    },

    '& .MuiSwitch-thumb': {
      boxShadow: 'none',
      boxSizing: 'border-box',
      ...commonThumbStyles(theme.palette.fg.quinary, dimensions.thumbSize),
    },
  };
});

const StyledControlLabel = styled(FormControlLabel)(({ color, labelPlacement }) => {
  return {
    ...(labelPlacement === 'start' ? { marginLeft: '0px' } : { marginLeft: '-8px' }),
    '& .MuiFormControlLabel-label': {
      fontWeight: 400,
      color: color ?? colors.grey[700],
    },
    '& .Mui-disabled.MuiFormControlLabel-label': {
      color: colors.grey[400],
      fontWeight: 600,
    },
  };
});
